import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Modal,
  Button,
  Container,
  Row,
  Col,
  Card,
  Form,
  Table,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import {
  BsPlusCircleFill,
  BsListCheck,
  BsArrowClockwise,
  BsTrash,
  BsPencilSquare,
  BsSearch,
  BsFileEarmarkExcel,
  BsArrowLeftRight,
  BsDashCircleFill,
} from "react-icons/bs";
import { FaHistory } from "react-icons/fa";
import { toast } from "react-toastify";
import DepartmentSidebar from "./components/DepertmentSidebar";
import DepartmentTopbar from "./components/DepartmentTopbar";

function FundList() {
  const [department, setDepartment] = useState("");
  const [newFundName, setNewFundName] = useState("");
  const [fundSources, setFundSources] = useState([]);
  const [fundHistory, setFundHistory] = useState([]);

  // Modals State
  const [showAddFundModal, setShowAddFundModal] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [amountToAdd, setAmountToAdd] = useState("");
  const [goNumber, setGONumber] = useState("");
  const [purpose, setPurpose] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0,16));
  const [showEditFundModal, setShowEditFundModal] = useState(false);
  const [fundNameToEdit, setFundNameToEdit] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fundToDelete, setFundToDelete] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedFundForHistory, setSelectedFundForHistory] = useState(null);
  const [singleFundHistory, setSingleFundHistory] = useState([]);

  // New State for History Edit and Delete
  const [showDeleteHistoryModal, setShowDeleteHistoryModal] = useState(false);
  const [historyEntryToDelete, setHistoryEntryToDelete] = useState(null);
  const [editingEntryIndex, setEditingEntryIndex] = useState(null);
  const [editingAmount, setEditingAmount] = useState("");

  // New State for Filters and Loading
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactionType, setTransactionType] = useState("all"); // 'all', 'credit', 'debit'
  const [searchTerm, setSearchTerm] = useState("");
  const [fundSearchTerm, setFundSearchTerm] = useState("");

  const userType = localStorage.getItem("type");
  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");
  const isChairman = userType === "chairman";
  const restrictedDepartments = ["F.O", "E.O", "MCIC"];
  const isRestricted = restrictedDepartments.includes(department);

  const fetchFundSources = async () => {
    try {
      if (!token) {
        toast.warn("Authorization token not found. Please log in again.");
        return;
      }
      const response = await axios.get(`${API_URL}fundlist.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const formattedFunds = response.data.funds.map((fund) => ({
          id: parseInt(fund.id, 10),
          name: fund.fund_name,
          total: parseFloat(fund.amount) || 0,
        }));
        setFundSources(formattedFunds);
      } else {
        toast.error(`Error fetching funds: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while fetching the fund sources.");
    }
  };

  const fetchFundHistory = async () => {
    setLoadingHistory(true);
    try {
      if (!token) {
        toast.warn("Authorization token not found. Please log in again.");
        return;
      }
      const response = await axios.get(`${API_URL}fundhistory.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const formattedHistory = response.data.fund_historys.map((entry) => ({
          id: parseInt(entry.id, 10),
          name: entry.fund_name,
          amount: parseFloat(entry.amount),
          date: entry.date,
          go_number: entry.go_number,
          purpose: entry.purpose,
          work_order: entry.work_order || null,
          type: entry.work_order ? "debit" : "credit",
        }));
        setFundHistory(formattedHistory);
      } else {
        setFundHistory([]);
      }
      if (!response.data.success) {
        toast.error(`Error fetching fund history: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while fetching the fund history.");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    setDepartment(localStorage.getItem("type") || "");
    fetchFundSources();
    fetchFundHistory();
  }, []);

  const handleCreateFundSource = async (e) => {
    e.preventDefault();
    if (!newFundName) return;
    try {
      const response = await axios.post(
        `${API_URL}addfund.php`,
        { fund_name: newFundName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Fund source created successfully!");
        setNewFundName("");
        fetchFundSources();
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while creating the fund source.");
    }
  };

  const handleDeleteFundSource = (fund) => {
    setFundToDelete(fund);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (fundToDelete) {
      try {
        const response = await axios.post(
          `${API_URL}deletefund.php`,
          { fund_id: fundToDelete.id.toString() },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.data.success) {
          toast.success("Fund deleted successfully!");
          fetchFundSources();
          fetchFundHistory();
        } else {
          toast.error(`Error deleting fund: ${response.data.message}`);
        }
      } catch (error) {
        toast.error("An error occurred while deleting the fund.");
      }
      setShowDeleteModal(false);
      setFundToDelete(null);
    }
  };

  const handleShowAddFundModal = (fund) => {
    setSelectedFund(fund);
    setShowAddFundModal(true);
  };

  const handleCloseAddFundModal = () => {
    setShowAddFundModal(false);
    setSelectedFund(null);
    setAmountToAdd("");
    setGONumber("");
    setPurpose("");
    setDate(new Date().toISOString().slice(0,16));
  };

  const handleAddFund = async (e) => {
    e.preventDefault();
    if (!selectedFund || !amountToAdd) return;
    try {
      const response = await axios.post(
        `${API_URL}addsinglefundamount.php`,
        {
          fund_id: selectedFund.id,
          amount: parseFloat(amountToAdd),
          go_number: goNumber,
          purpose: purpose,
          date: date,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === "success") {
        toast.success("Fund added successfully!");
        fetchFundSources();
        fetchFundHistory();
        handleCloseAddFundModal();
      } else {
        toast.error(`Error adding fund: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while adding the fund.");
    }
  };

  const handleShowEditModal = (fund) => {
    setSelectedFund(fund);
    setFundNameToEdit(fund.name);
    setShowEditFundModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditFundModal(false);
    setSelectedFund(null);
    setFundNameToEdit("");
  };

  const handleUpdateFundName = async (e) => {
    e.preventDefault();
    if (!selectedFund || !fundNameToEdit) return;
    try {
      const response = await axios.post(
        `${API_URL}editfund.php`,
        {
          fund_id: selectedFund.id.toString(),
          fund_name: fundNameToEdit,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.success) {
        toast.success("Fund name updated successfully!");
        fetchFundSources();
        fetchFundHistory();
        handleCloseEditModal();
      } else {
        toast.error(`Error updating fund: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while updating the fund name.");
    }
  };

  const handleShowHistoryModal = async (fund) => {
    setSelectedFundForHistory(fund);
    try {
      const response = await axios.post(
        `${API_URL}singlefundhistory.php`,
        { fund_id: fund.id.toString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const history = response.data.history || [];
        // Sort by date descending to show newest entries first
        const sortedHistory = history.sort((a, b) => new Date(b.date) - new Date(a.date));
        setSingleFundHistory(sortedHistory);
      } else {
        setSingleFundHistory([]);
      }
    } catch (error) {
      setSingleFundHistory([]);
    }
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedFundForHistory(null);
    setSingleFundHistory([]);
  };

  // Handlers for History Edit and Delete
  const handleStartEdit = (index) => {
    setEditingEntryIndex(index);
    setEditingAmount(singleFundHistory[index].amount.toString());
  };

  const handleSaveEdit = async () => {
    try {
      const entry = singleFundHistory[editingEntryIndex];
      const response = await axios.post(
        `${API_URL}updatefundhistory.php`,
        {
          id: entry.id,
          amount: parseFloat(editingAmount),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("History entry updated successfully!");
        fetchFundSources();
        fetchFundHistory();
        handleShowHistoryModal(selectedFundForHistory);
        setEditingEntryIndex(null);
        setEditingAmount("");
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while updating the history entry.");
    }
  };

  const handleCancelEdit = () => {
    setEditingEntryIndex(null);
    setEditingAmount("");
  };

  const handleShowDeleteHistoryModal = (entry) => {
    setHistoryEntryToDelete(entry);
    setShowDeleteHistoryModal(true);
  };

  const confirmDeleteHistory = async () => {
    if (historyEntryToDelete) {
      try {
        const response = await axios.post(
          `${API_URL}deletefundhistory.php`,
          { id: historyEntryToDelete.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          toast.success("History entry deleted successfully!");
          fetchFundSources();
          fetchFundHistory();
          handleShowHistoryModal(selectedFundForHistory);
          setShowDeleteHistoryModal(false);
          setHistoryEntryToDelete(null);
        } else {
          toast.error(`Error deleting history entry: ${response.data.message}`);
        }
      } catch (error) {
        toast.error("An error occurred while deleting the history entry.");
      }
    }
  };

  const filteredFundHistory = useMemo(() => {
    return fundHistory.filter((item) => {
      const itemDate = new Date(item.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;

      if (transactionType !== "all" && item.type !== transactionType)
        return false;

        const search = searchTerm.toLowerCase();
        if (
          search &&
          !item.name.toLowerCase().includes(search) &&
          !(item.work_order && item.work_order.toLowerCase().includes(search)) &&
          !(item.go_number && item.go_number.toLowerCase().includes(search)) &&
          !(item.purpose && item.purpose.toLowerCase().includes(search))
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date ascending (oldest first)
  }, [fundHistory, startDate, endDate, transactionType, searchTerm]);

  const { totalCredit, totalDebit } = useMemo(() => {
    return filteredFundHistory.reduce(
      (acc, item) => {
        if (item.type === "credit") acc.totalCredit += item.amount;
        else acc.totalDebit += item.amount;
        return acc;
      },
      { totalCredit: 0, totalDebit: 0 }
    );
  }, [filteredFundHistory]);

  const filteredFundSources = useMemo(() => {
    if (!fundSearchTerm) return fundSources;
    const search = fundSearchTerm.toLowerCase();
    return fundSources.filter(fund => fund.name.toLowerCase().includes(search));
  }, [fundSources, fundSearchTerm]);

  const handleExport = () => {
    const dataToExport = filteredFundHistory.map((item) => ({
      "Fund Source": item.name,
      Type: item.type.charAt(0).toUpperCase() + item.type.slice(1),
      Amount: item.amount,
      Date: item.date,
      "G.O Number": item.go_number || "N/A",
      Purpose: item.purpose || "N/A",
      "Work Order": item.work_order || "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FundHistory");
    XLSX.writeFile(workbook, "Fund_History_Report.xlsx");
  };

  const historyColumns = [
    {
      name: "Fund Source",
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Type",
      selector: (row) => row.type,
      sortable: true,
      cell: (row) => (
        <span
          className={`badge bg-${row.type === "credit" ? "success" : "danger"}`}
        >
          {row.type.charAt(0).toUpperCase() + row.type.slice(1)}
        </span>
      ),
    },
    {
      name: "Amount (₹)",
      selector: (row) => row.amount,
      sortable: true,
      right: true,
      cell: (row) => row.amount.toLocaleString("en-IN"),
    },
    { name: "Date", selector: (row) => row.date, sortable: true },
    { name: "G.O Number", selector: (row) => row.go_number || "N/A", sortable: true },
    {
      name: "Purpose",
      selector: (row) => row.purpose,
      sortable: true,
      grow: 3,
      cell: (row) => (
        <div title={row.purpose}>
          {row.purpose && row.purpose.length > 30
            ? `${row.purpose.substring(0, 30)}...`
            : row.purpose || "N/A"}
        </div>
      ),
    },
    {
      name: "Work Order",
      selector: (row) => row.work_order || "N/A",
      sortable: true,
      grow: 2,
    },
  ];

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <Container fluid className="p-4 bg-light">
          <header className="mb-4">
            <h1 className="h3 d-flex align-items-center gap-2">
              Fund Management
            </h1>
          </header>

          <Row className="g-4 mb-4">
            {!isRestricted && !isChairman && (
              <Col lg={4}>
                <Card className="shadow-sm">
                  <Card.Header
                    as="h5"
                    className="d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#e3f2fd",
                      borderBottom: "2px solid #1976d2",
                    }}
                  >
                    <BsPlusCircleFill style={{ color: "#1976d2" }} />{" "}
                    <span style={{ color: "#1976d2" }}>
                      Create New Fund Source
                    </span>
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={handleCreateFundSource}>
                      <Form.Group className="mb-3" controlId="newFundName">
                        <Form.Label>Fund Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={newFundName}
                          onChange={(e) => setNewFundName(e.target.value)}
                          placeholder="Enter New Fund name"
                          required
                        />
                      </Form.Group>
                      <div className="d-grid">
                        <Button variant="success" type="submit">
                          Create Fund Source
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            )}
            <Col lg={isRestricted || isChairman ? 12 : 8}>
              <Card className="h-100 shadow-sm">
                <Card.Header
                  as="h5"
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    backgroundColor: "#e3f2fd",
                    borderBottom: "2px solid #1976d2",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <BsListCheck style={{ color: "#1976d2" }} />
                    <span style={{ color: "#1976d2" }}>
                      Available Fund Sources
                    </span>
                  </div>
                  <div style={{ width: '250px' }}>
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <BsSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Search funds..."
                        value={fundSearchTerm}
                        onChange={(e) => setFundSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover responsive="sm">
                      <thead
                        className="table-dark"
                        style={{ backgroundColor: "#0d6efd" }}
                      >
                        <tr>
                          <th>Fund Name</th>
                          <th>Total Amount (₹)</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFundSources.map((fund) => (
                          <tr key={fund.id}>
                            <td>{fund.name}</td>
                            <td>{fund.total.toLocaleString()}</td>
                            <td className="d-flex gap-2">
                              {!isRestricted && !isChairman && (
                                <>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleShowAddFundModal(fund)}
                                  >
                                    <BsPlusCircleFill className="me-1" /> Add
                                    Fund
                                  </Button>
                                  <Button
                                    variant="warning"
                                    size="sm"
                                    onClick={() => handleShowEditModal(fund)}
                                  >
                                    <BsPencilSquare />
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="info"
                                size="sm"
                                onClick={() => handleShowHistoryModal(fund)}
                              >
                                <FaHistory />
                              </Button>
                              {!isRestricted && !isChairman && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDeleteFundSource(fund)}
                                >
                                  <BsTrash />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm">
            <Card.Header
              as="h5"
              className="d-flex align-items-center gap-2"
              style={{
                backgroundColor: "#e3f2fd",
                borderBottom: "2px solid #1976d2",
              }}
            >
              <BsArrowClockwise style={{ color: "#1976d2" }} />{" "}
              <span style={{ color: "#1976d2" }}>Fund Transaction History</span>
            </Card.Header>
            <Card.Body>
              <div className="p-3 border-bottom bg-light rounded mb-4">
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Search</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <BsSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col className="text-end">
                    <Button onClick={handleExport} variant="success">
                      <BsFileEarmarkExcel className="me-2" />
                      Export to Excel
                    </Button>
                  </Col>
                </Row>
              </div>

              <Row className="px-3 text-center g-3 mb-4">
                <Col md={4}>
                  <Card className="shadow-sm border-success">
                    <Card.Body>
                      <Card.Title className="text-success d-flex align-items-center justify-content-center gap-2">
                        <BsPlusCircleFill className="text-success" />
                        Total Credit
                      </Card.Title>
                      <Card.Text className="fs-4 fw-bold text-success">
                        ₹{totalCredit.toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="shadow-sm border-danger">
                    <Card.Body>
                      <Card.Title className="text-danger d-flex align-items-center justify-content-center gap-2">
                        <BsDashCircleFill className="text-danger" />
                        Total Debit
                      </Card.Title>
                      <Card.Text className="fs-4 fw-bold text-danger">
                        ₹{Math.abs(totalDebit).toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="shadow-sm border-primary">
                    <Card.Body>
                      <Card.Title className="text-primary d-flex align-items-center justify-content-center gap-2">
                        <BsArrowLeftRight className="text-primary" />
                        Net Balance
                      </Card.Title>
                      <Card.Text className="fs-4 fw-bold text-primary">
                        ₹{(totalCredit + totalDebit).toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <DataTable
                columns={historyColumns}
                data={filteredFundHistory}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                responsive
                highlightOnHover
                progressPending={loadingHistory}
                progressComponent={
                  <Spinner animation="border" variant="primary" />
                }
              />
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* All Modals */}
      <Modal show={showAddFundModal} onHide={handleCloseAddFundModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <BsPlusCircleFill />
              <span>Add Fund</span>
            </div>
            <small
              className="text-muted d-block mt-1"
              style={{ wordBreak: "break-word" }}
            >
              {selectedFund?.name}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddFund}>
            <Form.Group className="mb-3" controlId="amountToAdd">
              <Form.Label>Amount (₹)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                value={amountToAdd}
                onChange={(e) => {
                  const { value } = e.target;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setAmountToAdd(value);
                  }
                }}
                placeholder="Enter amount to add"
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="goNumber">
              <Form.Label>G.O number</Form.Label>
              <Form.Control
                type="text"
                value={goNumber}
                onChange={(e) => setGONumber(e.target.value)}
                placeholder="Enter G.O number here"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="purpose">
              <Form.Label>Purpose</Form.Label>
              <Form.Control
                type="text"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Explain purpose..."
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="dateTimeToAdd">
              <Form.Label>Date & Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </Form.Group>
            <div className="d-grid">
              <Button variant="primary" type="submit">
                Confirm Add Fund
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEditFundModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <BsPencilSquare />
              <span>Edit Fund Name</span>
            </div>
            <small
              className="text-muted d-block mt-1"
              style={{ wordBreak: "break-word" }}
            >
              {selectedFund?.name}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateFundName}>
            <Form.Group className="mb-3" controlId="fundNameToEdit">
              <Form.Label>New Fund Name</Form.Label>
              <Form.Control
                type="text"
                value={fundNameToEdit}
                onChange={(e) => setFundNameToEdit(e.target.value)}
                placeholder="Enter new fund name"
                required
                autoFocus
              />
            </Form.Group>
            <div className="d-grid">
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop={true}
        backdropClassName="modal-backdrop-custom"
        style={{
          zIndex: 1050
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <BsTrash style={{ color: "#dc3545" }} /> Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the fund source "
            <strong>{fundToDelete?.name}</strong>"?
          </p>
          <p className="text-danger">
            This will also remove all associated history and the action cannot
            be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete Fund
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteHistoryModal}
        onHide={() => setShowDeleteHistoryModal(false)}
        centered
        backdrop={true}
        backdropClassName="modal-backdrop-delete"
        style={{
          zIndex: 1060
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <BsTrash style={{ color: "#dc3545" }} /> Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete this history entry?
          </p>
          <p className="text-danger">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteHistoryModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteHistory}>
            Delete Entry
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showHistoryModal}
        onHide={handleCloseHistoryModal}
        centered
        size="lg"
        style={{
          zIndex: 1050
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <FaHistory />
              <span>Fund History</span>
            </div>
            <small
              className="text-muted d-block mt-1"
              style={{ wordBreak: "break-word" }}
            >
              {selectedFundForHistory?.name}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive" style={{ maxHeight: "400px" }}>
            <Table striped bordered hover responsive="sm">
              <thead className="table-dark">
                <tr>
                  <th>Amount Added (₹)</th>
                  <th>Date</th>
                  <th>G.O Number</th>
                  <th>Purpose</th>
                  <th>Work Order</th>
                  {userType === "Accountant" && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {singleFundHistory.length > 0 ? (
                  singleFundHistory.map((entry, index) => (
                    <tr key={index}>
                      <td>
                        {editingEntryIndex === index ? (
                          <Form.Control
                            type="number"
                            value={editingAmount}
                            onChange={(e) => setEditingAmount(e.target.value)}
                            size="sm"
                          />
                        ) : (
                          <span className="fw-bold">{entry.amount.toLocaleString()}</span>
                        )}
                      </td>
                      <td>{entry.date}</td>
                      <td>{entry.go_number || "N/A"}</td>
                      <td title={entry.purpose}>
                        {entry.purpose && entry.purpose.length > 30
                          ? `${entry.purpose.substring(0, 30)}...`
                          : entry.purpose || "N/A"}
                      </td>
                      <td>{entry.work_order || "NIL"}</td>
                      {userType === "Accountant" && (
                        <td>
                          <div className="d-flex gap-2">
                            {editingEntryIndex === index ? (
                              <>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={handleSaveEdit}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={handleCancelEdit}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="warning"
                                  size="sm"
                                  onClick={() => handleStartEdit(index)}
                                >
                                  <BsPencilSquare />
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleShowDeleteHistoryModal(entry)}
                                >
                                  <BsTrash />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={userType === "Accountant" ? "6" : "5"} className="text-center">
                      No history found for this fund.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseHistoryModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FundList;
